<script setup lang="ts">
  import { ref, onMounted, onUnmounted, watchEffect } from 'vue';

  import { type Call } from '@bv/stores';

  const props = defineProps<{
    call: Call;
  }>();

  /**
   * Audio Visualizer Component
   *
   * A component that visualizes the audio waveform from an active SIP call.
   * Automatically responds to call changes and cleans up resources when unmounted.
   */
  const canvas = ref<HTMLCanvasElement | null>(null);

  // Audio analysis variables
  let audioContext: AudioContext | null = null;
  let analyser: AnalyserNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let animationFrameId: number = 0;

  /**
   * Sets up the canvas dimensions and scaling for high DPI displays
   */
  const setupCanvas = (): void => {
    if (!canvas.value) {
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.value.getBoundingClientRect();

    canvas.value.width = rect.width * dpr;
    canvas.value.height = rect.height * dpr;

    const ctx = canvas.value.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, rect.width, rect.height);
    }
  };

  /**
   * Initializes the audio analyzer with the provided media stream
   * @param {MediaStream} stream - The audio stream to analyze
   */
  const initAudioAnalyzer = (stream: MediaStream): void => {
    cleanup();

    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      drawWaveform();
    } catch (error) {
      cleanup();
      throw error;
    }
  };

  /**
   * Draws the audio waveform on the canvas
   */
  const drawWaveform = (): void => {
    if (!canvas.value || !analyser) {
      return;
    }

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteTimeDomainData(dataArray);

    const ctx = canvas.value.getContext('2d');
    if (!ctx) {
      return;
    }

    const width = canvas.value.width;
    const height = canvas.value.height;

    // Clear the canvas
    ctx.clearRect(0, 0, width, height);

    // Set line style
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#fff5';
    ctx.beginPath();

    // Draw waveform
    const sliceWidth = width / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 128.0;
      const y = (v * height) / 2;

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    ctx.lineTo(width, height / 2);
    ctx.stroke();

    animationFrameId = requestAnimationFrame(drawWaveform);
  };

  /**
   * Cleans up all resources (audio context, analyser, animation frame)
   */
  const cleanup = (): void => {
    // Cancel animation frame
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = 0;
    }

    // Disconnect audio nodes
    if (source) {
      source.disconnect();
      source = null;
    }

    if (analyser) {
      analyser.disconnect();
      analyser = null;
    }

    // Close audio context
    if (audioContext?.state !== 'closed') {
      audioContext?.close().catch((error: Error) => {
        throw error;
      });
      audioContext = null;
    }

    // Clear canvas
    if (canvas.value) {
      const ctx = canvas.value.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
        ctx.beginPath();
      }
    }
  };

  // Watch for changes in the active call's audio stream
  watchEffect(() => {
    if (props.call.audioStream) {
      initAudioAnalyzer(props.call.audioStream);
    } else {
      cleanup();
    }
  });

  onMounted(() => {
    // Initialize canvas and set up resize listener when component mounts
    setupCanvas();
    window.addEventListener('resize', setupCanvas);
  });

  onUnmounted(() => {
    // Clean up all resources when component unmounts
    cleanup();
    window.removeEventListener('resize', setupCanvas);
  });
</script>

<template>
  <canvas ref="canvas" />
</template>

<style lang="scss" scoped>
  canvas {
    width: 100%;
    height: 100%;
  }
</style>
